import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  MessageSquare,
  Table2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  FileText,
  Download,
} from "lucide-react";
import { api, apiUrl, authToken } from "../api";
import { Button, StatusBadge, statusLabel } from "../ui";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";
import { katalog, useT } from "../i18n";
import DatasetWizard from "../components/DatasetWizard";
import type { Figure, Table } from "../components/source-pane/types";

type EmbeddingStatus =
  | "pending"
  | "doc_ai_pending"
  | "processing"
  | "done"
  | "error";

const ACTIVE_STATUSES: EmbeddingStatus[] = [
  "pending",
  "doc_ai_pending",
  "processing",
];

type SummaryStatus = "pending" | "processing" | "done" | "error";

interface SuggestedSchema {
  title?: string;
  row_unit?: string;
  columns?: { name: string; type: string; description?: string }[];
  strategy?: string;
  // v2 (dataset-extraction-v2-plan, API-Vertrag): origin unterscheidet
  // LLM-Vorschlag von code-generierter Tabelle; table_page nur bei origin=table.
  origin?: "llm" | "table";
  estimated_row_count?: number | null;
  table_page?: number | null;
}

// Response von POST /documents/{id}/one-click-table (immer Liste, auch bei single).
interface CreatedDataset {
  id: string;
  name: string;
  status: string;
  origin?: string;
}

interface DocDetail {
  id: string;
  title: string;
  source_pdf_url: string | null;
  embedding_status: EmbeddingStatus;
  summary: string | null;
  summary_status: SummaryStatus;
  suggested_schemas: SuggestedSchema[] | null;
  extract_strategy?: "text" | "vision";
  chunk_count: number;
  project_id: string | null;
  created_by: string;
  created_at: string;
  markdown: string;
  brain_store_doc_id: number | null;
  figures: Figure[];
  tables: Table[];
  // Zähler der von doc-ai erkannten Tabellen (der Detail-Endpoint liefert seit
  // der /search-Migration keine Tabellen-Liste mehr, nur diesen Zähler).
  detected_table_count: number;
}

interface ChatRow {
  id: string;
}

export default function DocumentDetailPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const modal = useModal();
  const t = useT();
  const [doc, setDoc] = useState<DocDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [creatingIndex, setCreatingIndex] = useState<number | null>(null);
  const [creatingAll, setCreatingAll] = useState(false);
  const [created, setCreated] = useState<CreatedDataset[]>([]);
  const wasActiveRef = useRef(false);

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const load = async () => {
      try {
        const d = await api.get<DocDetail>(`/documents/${documentId}`);
        if (cancelled) return;
        setDoc(d);
        setLoadError(null);
        const isActive = ACTIVE_STATUSES.includes(d.embedding_status);
        const summaryActive =
          d.summary_status === "pending" || d.summary_status === "processing";
        if (wasActiveRef.current && !isActive && d.embedding_status === "done") {
          // `katalog()` statt `t`: dieses `load` hängt an [documentId] und ruft
          // sich per setTimeout selbst wieder auf — es hält also den Katalog des
          // Renders fest, in dem es erzeugt wurde (docs/i18n.md §3).
          const tk = katalog();
          const counts: string[] = [];
          if (d.figures.length) counts.push(tk.documents.bilderAnzahl(d.figures.length));
          if (d.tables.length) counts.push(tk.documents.tabellenAnzahl(d.tables.length));
          if (counts.length) toast.ok(tk.documents.verarbeitungFertig, counts.join(" · "));
        }
        wasActiveRef.current = isActive;
        if (isActive || summaryActive) {
          const ageMs = Date.now() - new Date(d.created_at).getTime();
          const interval =
            ageMs < 60_000 ? 5_000 : ageMs < 180_000 ? 10_000 : 30_000;
          timer = setTimeout(load, interval);
        }
      } catch (e) {
        if (cancelled) return;
        const msg = (e as Error).message;
        setLoadError(msg);
        if (!doc) toast.err(katalog().documents.dokumentNichtGefunden, msg);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // Response ist IMMER eine Liste ({"datasets":[…]}) — auch bei single genau 1.
  const mergeCreated = (incoming: CreatedDataset[]) =>
    setCreated((prev) => {
      const byId = new Map(prev.map((d) => [d.id, d]));
      for (const d of incoming) byId.set(d.id, d);
      return Array.from(byId.values());
    });

  const createOne = async (schemaIndex: number) => {
    if (!doc) return;
    setCreatingIndex(schemaIndex);
    try {
      const res = await api.post<{ datasets: CreatedDataset[] }>(
        `/documents/${doc.id}/one-click-table`,
        { schema_index: schemaIndex, mode: "single" }
      );
      mergeCreated(res.datasets ?? []);
    } catch (e) {
      toast.err(t.documents.tabelleNichtErstellt, (e as Error).message);
    } finally {
      setCreatingIndex(null);
    }
  };

  const createAll = async () => {
    if (!doc) return;
    setCreatingAll(true);
    try {
      const res = await api.post<{ datasets: CreatedDataset[] }>(
        `/documents/${doc.id}/one-click-table`,
        { schema_index: null, mode: "all" }
      );
      const list = res.datasets ?? [];
      mergeCreated(list);
      toast.ok(
        t.documents.tabellenErstellt(list.length),
        t.documents.extraktionImHintergrund
      );
    } catch (e) {
      toast.err(t.documents.tabellenNichtErstellt, (e as Error).message);
    } finally {
      setCreatingAll(false);
    }
  };

  const chatAbout = async () => {
    if (!doc) return;
    try {
      const chat = await api.post<ChatRow>("/chats", {
        project_id: doc.project_id,
        // Chat-TITEL (Inhalt, kein Etikett), deutsch wie die noch nicht
        // umgestellte Chat-Oberfläche (docs/i18n.md §5) — nicht wegen der
        // Auto-Betitelung, die seit 680ee5e der Einstellung folgt.
        title: `Über: ${doc.title}`,
      });
      navigate(`/chat/${chat.id}`);
    } catch (e) {
      toast.err(t.documents.chatErstellungFehlgeschlagen, (e as Error).message);
    }
  };

  const del = async () => {
    if (!doc) return;
    const ok = await modal.confirm({
      title: t.documents.wirklichLoeschen(doc.title),
      description: t.documents.chunksUndVektorenKurz,
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.ok(t.documents.dokumentGeloescht);
      navigate("/docs");
    } catch (e) {
      toast.err(t.documents.loeschenFehlgeschlagen, (e as Error).message);
    }
  };

  if (!doc) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-text-2">
        {loadError ? (
          <span className="inline-flex items-center gap-2 text-danger">
            <AlertCircle className="w-4 h-4" /> {loadError}
          </span>
        ) : (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {t.documents.laden}
          </>
        )}
      </div>
    );
  }

  const isActive = ACTIVE_STATUSES.includes(doc.embedding_status);
  const summaryPending =
    doc.summary_status === "pending" || doc.summary_status === "processing";
  const schemas = doc.suggested_schemas ?? [];
  const hasSchema = schemas.length > 0;
  // Schema-Flut-Hinweis: das Backend cappt die Vorschläge (2-5), bei
  // tabellenreichen Docs bleiben also erkannte Tabellen ohne Vorschlag übrig.
  // N = erkannte Tabellen (detected_table_count, aus doc-ai) − vorgeschlagene
  // Schemas (rein zahl-basiert; keine Per-Tabelle-Coverage-Map). >0 → Lücke
  // sichtbar machen.
  const uncoveredTableCount = Math.max(
    0,
    (doc.detected_table_count ?? 0) - schemas.length
  );
  const canCreateTable =
    doc.embedding_status === "done" &&
    doc.summary_status === "done" &&
    hasSchema;
  const createTableTitle = summaryPending
    ? t.documents.wartenAufZusammenfassung
    : doc.embedding_status !== "done"
      ? t.documents.dokumentWirdVerarbeitet
      : !hasSchema
        ? t.documents.keinVorschlagVerfuegbar
        : undefined;
  const creating = creatingIndex !== null || creatingAll;

  return (
    <div className="flex flex-col md:grid md:grid-cols-[1fr_300px] h-full overflow-y-auto md:overflow-hidden">
      {/* Left: body */}
      {/* Mobil: shrink-0 + min-h auto, sonst kollabiert die Section im h-full-
          Flex-Root unter ihre Content-Höhe und der Inhalt malt über die Aside.
          Desktop (md:grid): min-h-0 nötig, damit die Pane intern scrollt. */}
      <section className="flex flex-col shrink-0 md:shrink md:min-h-0 border-b md:border-b-0 md:border-r border-border">
        <header className="px-4 md:px-6 py-4 border-b border-border flex items-center gap-3">
          <Link
            to="/docs"
            className="p-1 text-text-2 hover:text-text"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <h1 className="font-semibold text-lg truncate flex-1">{doc.title}</h1>
        </header>
        <div className="md:flex-1 md:overflow-y-auto px-4 md:px-8 py-6 min-w-0">
          {isActive ? (
            <ProcessingSkeleton
              status={doc.embedding_status}
              createdAt={doc.created_at}
            />
          ) : doc.embedding_status === "error" ? (
            <div className="max-w-2xl mx-auto bg-danger-soft border border-danger/30 rounded-md p-4">
              <div className="flex items-center gap-2 text-danger font-medium mb-1">
                <AlertCircle className="w-4 h-4" /> {t.documents.verarbeitungFehlgeschlagen}
              </div>
              <p className="text-sm text-danger/80">{t.documents.docAiFehler}</p>
            </div>
          ) : (
            <OriginalFileView doc={doc} />
          )}
        </div>
      </section>

      {/* Right sidebar */}
      <aside className="md:overflow-y-auto p-4 space-y-5 bg-surface-2/60">
        <section className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
            {t.documents.aktionen}
          </h3>
          <Button
            variant="primary"
            onClick={chatAbout}
            className="w-full"
            icon={<MessageSquare className="w-4 h-4" />}
          >
            {t.documents.mitDokumentUnterhalten}
          </Button>
          <button
            onClick={() => setWizardOpen(true)}
            disabled={doc.embedding_status !== "done"}
            title={t.documents.anpassenTitel}
            className="w-full text-xs text-text-2 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed underline underline-offset-2 py-1 inline-flex items-center justify-center gap-1"
          >
            {t.documents.anpassen}
          </button>
          {summaryPending && (
            <div className="text-[11px] text-text-3 italic inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {t.documents.vorschlagWirdVorbereitet}
            </div>
          )}
          <button
            onClick={del}
            className="w-full text-sm text-danger hover:bg-danger-soft px-3 py-1.5 rounded-md inline-flex items-center justify-center gap-1.5"
          >
            <Trash2 className="w-4 h-4" />
            {t.common.loeschen}
          </button>
        </section>

        <section className="space-y-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
            {t.documents.zusammenfassung}
          </h3>
          {doc.summary_status === "done" && doc.summary ? (
            <div className="text-[13px] text-text leading-snug">
              {doc.summary}
            </div>
          ) : doc.summary_status === "pending" ||
            doc.summary_status === "processing" ? (
            <div className="text-[12px] text-text-3 italic inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {t.documents.wirdGeneriert}
            </div>
          ) : (
            <div className="text-[12px] text-text-3">
              {doc.summary_status === "error" ? t.documents.fehler : t.documents.keine}
            </div>
          )}
        </section>

        {hasSchema && (
          <section className="space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
                {schemas.length > 1
                  ? t.documents.tabellenVorschlaege
                  : t.documents.tabellenVorschlag}
              </h3>
              {schemas.length > 1 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={createAll}
                  disabled={!canCreateTable || creating}
                  title={createTableTitle}
                  loading={creatingAll}
                >
                  {t.documents.alleErstellen}
                </Button>
              )}
            </div>
            {schemas.map((s, idx) => (
              <SchemaCard
                key={idx}
                schema={s}
                creating={creatingIndex === idx}
                disabled={!canCreateTable || creating}
                title={createTableTitle}
                onCreate={() => createOne(idx)}
              />
            ))}
          </section>
        )}

        {uncoveredTableCount > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-border/70 bg-surface px-2.5 py-2 text-[12px] leading-snug text-text-2">
            <Table2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-3" />
            <div>
              <span className="tabular-nums text-text">{uncoveredTableCount}</span>{" "}
              {t.documents.weitereOhneSchema(uncoveredTableCount)}
              <span className="text-text-3">
                {" "}
                {t.documents.schemaSelbstDefinieren}
              </span>
            </div>
          </div>
        )}

        {created.length > 0 && (
          <section className="space-y-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
              {t.documents.erstellteTabellen}
            </h3>
            <div className="space-y-1">
              {created.map((d) => (
                <Link
                  key={d.id}
                  to={`/ds/${d.id}`}
                  className="flex items-center gap-1.5 text-[13px] text-brand-600 hover:underline"
                >
                  <Table2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate flex-1">{d.name}</span>
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
            {t.documents.metadaten}
          </h3>
          <dl className="text-[12px] space-y-1">
            <Row label={t.documents.status}>
              <StatusInline s={doc.embedding_status} />
            </Row>
            {doc.figures.length > 0 && (
              <Row label={t.documents.bilder}>
                <span className="tabular-nums">{doc.figures.length}</span>
              </Row>
            )}
            {doc.tables.length > 0 && (
              <Row label={t.documents.tabellen}>
                <span className="tabular-nums">{doc.tables.length}</span>
              </Row>
            )}
            <Row label={t.documents.hochgeladen}>{fmtUploaded(doc.created_at)}</Row>
            <Row label={t.documents.hochgeladenVon}>{doc.created_by}</Row>
            {doc.project_id && (
              <Row label={t.documents.projekt}>
                <Link
                  to={`/projects/${doc.project_id}`}
                  className="text-brand-600 hover:underline inline-flex items-center gap-0.5"
                >
                  {t.documents.ansehen} <ExternalLink className="w-2.5 h-2.5" />
                </Link>
              </Row>
            )}
          </dl>
        </section>
      </aside>

      <DatasetWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        doc={{ id: doc.id, title: doc.title, project_id: doc.project_id }}
      />
    </div>
  );
}

function SchemaCard({
  schema,
  creating,
  disabled,
  title,
  onCreate,
}: {
  schema: SuggestedSchema;
  creating: boolean;
  disabled: boolean;
  title?: string;
  onCreate: () => void;
}) {
  const t = useT();
  const colCount = schema.columns?.length ?? 0;
  return (
    <div className="space-y-1.5 rounded-md border border-border bg-surface p-2.5">
      <div className="flex items-start gap-2">
        <div className="text-[13px] font-medium text-text leading-snug flex-1 min-w-0">
          {schema.title || t.documents.tabelle}
        </div>
        <OriginBadge origin={schema.origin} tablePage={schema.table_page} />
      </div>
      {schema.row_unit && (
        <div className="text-[12px] text-text-2">{t.documents.zeileIst(schema.row_unit)}</div>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-text-3">
        {colCount > 0 && <span>{t.documents.spaltenAnzahl(colCount)}</span>}
        {schema.estimated_row_count != null && (
          <span>{t.documents.zeilenErwartet(schema.estimated_row_count)}</span>
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={onCreate}
        disabled={disabled}
        loading={creating}
        title={title}
        className="mt-0.5"
        icon={<Table2 className="w-3 h-3" />}
      >
        {t.documents.erstellen}
      </Button>
    </div>
  );
}

// origin=table → code-generierte Tabelle (mit Seitenzahl), origin=llm → KI-Schema.
// Fehlt origin (Alt-Docs vor v2), zeigen wir kein Badge.
function OriginBadge({
  origin,
  tablePage,
}: {
  origin?: string;
  tablePage?: number | null;
}) {
  const t = useT();
  if (origin === "table") {
    return (
      <StatusBadge status="neutral" className="shrink-0">
        {tablePage != null ? t.documents.tabelleSeite(tablePage) : t.documents.tabelle}
      </StatusBadge>
    );
  }
  if (origin === "llm") {
    return (
      <StatusBadge status="info" className="shrink-0">
        {t.documents.kiSchema}
      </StatusBadge>
    );
  }
  return null;
}

function OriginalFileView({ doc }: { doc: DocDetail }) {
  const t = useT();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const filename = doc.source_pdf_url?.split("/").pop() ?? doc.title;
  const isPdf = filename.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    (async () => {
      try {
        const t = authToken();
        const r = await fetch(apiUrl(`/documents/${doc.id}/raw`), {
          headers: t ? { Authorization: `Bearer ${t}` } : undefined,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.id]);

  return (
    <div className="h-full flex flex-col gap-4 max-w-4xl mx-auto">
      <div className="flex items-start gap-2.5 bg-success-soft border border-success/30 rounded-md p-3">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-success" />
        <div>
          <div className="font-medium text-sm text-success">
            {t.documents.inhaltExtrahiert}
          </div>
          <p className="text-xs text-success/80 mt-0.5">
            {t.documents.inhaltExtrahiertHinweis}
          </p>
        </div>
      </div>

      {err ? (
        <div className="text-sm text-danger">
          {t.documents.originalNichtGeladen(err)}
        </div>
      ) : !blobUrl ? (
        <div className="flex items-center gap-2 text-sm text-text-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {t.documents.ladeOriginal}
        </div>
      ) : isPdf ? (
        <div className="flex-1 min-h-[600px] flex flex-col rounded-md border border-border overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-surface-2">
            <div className="text-xs text-text-2 truncate inline-flex items-center gap-1.5 min-w-0">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{filename}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
              >
                {t.documents.inNeuemTab} <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href={blobUrl}
                download={filename}
                className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> {t.common.herunterladen}
              </a>
            </div>
          </div>
          <iframe
            src={blobUrl}
            title={filename}
            className="flex-1 min-h-[560px] w-full"
          />
        </div>
      ) : (
        <DownloadCard blobUrl={blobUrl} filename={filename} />
      )}
    </div>
  );
}

function DownloadCard({
  blobUrl,
  filename,
}: {
  blobUrl: string;
  filename: string;
}) {
  const t = useT();
  return (
    <div className="max-w-md mx-auto text-center py-10 px-6 border border-border rounded-lg">
      <FileText className="w-10 h-10 mx-auto mb-3 text-text-3" />
      <div className="text-sm font-medium mb-1 truncate">{filename}</div>
      <p className="text-xs text-text-2 mb-4">{t.documents.keineVorschau}</p>
      <a
        href={blobUrl}
        download={filename}
        className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-md"
      >
        <Download className="w-4 h-4" /> {t.common.herunterladen}
      </a>
    </div>
  );
}

function ProcessingSkeleton({
  status,
  createdAt,
}: {
  status: EmbeddingStatus;
  createdAt: string;
}) {
  const t = useT();
  const [elapsed, setElapsed] = useState(() =>
    Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000)
  );
  useEffect(() => {
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [createdAt]);

  const label =
    status === "doc_ai_pending"
      ? t.documents.docAiVerarbeitet
      : status === "processing"
        ? t.documents.embeddingsWerdenBerechnet
        : t.documents.wartetAufDocAi;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="bg-warning-soft border border-warning/30 rounded-md p-4">
        <div className="flex items-center gap-2 text-warning font-medium">
          <Loader2 className="w-4 h-4 animate-spin" />
          {label}
        </div>
        <p className="text-xs text-warning/80 mt-1.5">
          {t.documents.verarbeitetSeit(formatElapsed(elapsed))}
        </p>
      </div>

      <div className="space-y-3">
        <div className="h-5 bg-surface-3 rounded-md animate-pulse w-2/3" />
        <div className="h-3 bg-surface-3 rounded-md animate-pulse" />
        <div className="h-3 bg-surface-3 rounded-md animate-pulse w-11/12" />
        <div className="h-3 bg-surface-3 rounded-md animate-pulse w-5/6" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SkeletonFigure />
        <SkeletonFigure />
      </div>

      <div className="space-y-3">
        <div className="h-3 bg-surface-3 rounded-md animate-pulse w-3/4" />
        <div className="h-3 bg-surface-3 rounded-md animate-pulse" />
        <div className="h-3 bg-surface-3 rounded-md animate-pulse w-2/3" />
      </div>
    </div>
  );
}

function SkeletonFigure() {
  return (
    <div className="space-y-2">
      <div className="aspect-[4/3] bg-surface-3 rounded-md animate-pulse" />
      <div className="h-2 bg-surface-3 rounded-md animate-pulse w-1/3" />
      <div className="h-2.5 bg-surface-3 rounded-md animate-pulse" />
    </div>
  );
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

// Ausgeschriebenes Datum + Uhrzeit, z. B. „5. Juli 2026, 21:41".
function fmtUploaded(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date}, ${time}`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-text-2">{label}</dt>
      <dd className="text-text text-right">{children}</dd>
    </div>
  );
}

function StatusInline({ s }: { s: EmbeddingStatus }) {
  const info = statusLabel(s, { domain: "document" });
  return <StatusBadge status={info.status}>{info.label}</StatusBadge>;
}
